import { useState } from "react";
import { motion } from "framer-motion";
import { Instagram, Youtube, Facebook } from "lucide-react";
import CategoryHero from "./CategoryHero";
import NewsTicker from "./NewsTicker";
import { useManagedCollection, slugify } from "../admin/useManagedCollection";
import { AdminItem, AdminAddBar } from "../admin/AdminControls";
import type { FieldDef } from "../admin/AdminFieldModal";
import SocialLinks, { SOCIAL_FIELDS, normalizeSocialUrl } from './SocialLinks';

const DJ_FIELDS: FieldDef[] = [
  { key: "name", label: "DJ name", required: true },
  { key: "contactUrl", label: "Contact URL (Instagram/Facebook/YouTube)", type: "url", placeholder: "https://…" },
  { key: "image", label: "Image URL", type: "image", placeholder: "https://…" },
  ...SOCIAL_FIELDS,
];

export interface DJ {
  name: string;
  contactUrl?: string;
  image: string;
}

export const DJS: DJ[] = [
  {
    name: "DJ Veer",
    contactUrl: "https://www.instagram.com/djveerhere/",
    image: "https://storage.googleapis.com/happyspace-generated-images/generated/2026-06-24/507384bd7312.png",
  },
  {
    name: "DJ Mini",
    contactUrl: "https://www.instagram.com/djminidallas/",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783529120673-bqfcr.png",
  },
  {
    name: "DJ Diva",
    contactUrl: "https://www.instagram.com/djdivaone/",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783529319230-dd5ke.png",
  },
  {
    name: "DJ Charan",
    contactUrl: "https://www.instagram.com/djcharann/",
    image: "https://storage.googleapis.com/happyspace-generated-images/generated/2026-06-24/507384bd7312.png",
  },
  {
    name: "DJ Mahesh",
    contactUrl: "https://www.instagram.com/djmsharp/",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783533939552-93mwft.png",
  },
  {
    name: "DJ RAJ USA",
    contactUrl: "https://www.instagram.com/djrajusa",
    image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-07-12/d600949ddecd.jpeg",
  },
  {
    name: "VDJ Dimple",
    contactUrl: "https://www.instagram.com/djdimple_/",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784660365834-ko3gh9.png",
  },
];

// Picks the right icon/label/color for the card's contactUrl based on its domain,
// so the big button always matches the platform it actually links to.
function contactButtonMeta(url: string) {
  const u = url.toLowerCase();
  if (u.includes('facebook.com')) {
    return { label: 'Facebook', Icon: Facebook, cls: 'bg-[#1877F2] hover:bg-[#166fe0]' };
  }
  if (u.includes('youtube.com') || u.includes('youtu.be')) {
    return { label: 'YouTube', Icon: Youtube, cls: 'bg-[#FF0000] hover:bg-[#e60000]' };
  }
  return { label: 'Instagram', Icon: Instagram, cls: 'bg-gradient-to-tr from-[#feda75] via-[#d62976] to-[#4f5bd5] hover:opacity-90' };
}

const FALLBACK_IMAGE = "https://storage.googleapis.com/happyspace-generated-images/generated/2026-06-24/507384bd7312.png";

export function DJCard({ dj, index, href }: { dj: DJ; index: number; href?: string }) {
  const contactUrl = dj.contactUrl && dj.contactUrl !== '#' ? dj.contactUrl : null;
  const meta = contactUrl ? contactButtonMeta(contactUrl) : null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      className="bg-white rounded-md border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col"
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      <div className="w-full aspect-square relative overflow-hidden bg-gray-50">
        <img
          src={dj.image}
          alt={dj.name}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={(e) => {
            (e.target as HTMLImageElement).src = FALLBACK_IMAGE;
          }}
        />
      </div>
      <div className="p-1.5 pb-0.5 flex-1">
        <h3 className="text-[10px] font-bold text-gray-900 leading-tight truncate">
          {href ? (
            <a href={href} className="hover:text-[#f97316] transition-colors">
              {dj.name}
            </a>
          ) : (
            dj.name
          )}
        </h3>
      </div>
      <div className="px-1.5 pb-1 pt-0.5 flex justify-center">
        <SocialLinks item={dj} px={18} />
      </div>
      <div className="px-1.5 pb-1.5">
        {meta ? (
          <a
            href={normalizeSocialUrl(contactUrl) || contactUrl!}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className={`w-full flex items-center justify-center gap-1 text-white text-[9px] font-semibold py-1 rounded transition-opacity duration-200 ${meta.cls}`}
          >
            <meta.Icon className="w-2.5 h-2.5" /> {meta.label}
          </a>
        ) : (
          <span className="w-full flex items-center justify-center gap-1 bg-gray-50 border border-gray-200 text-gray-400 text-[9px] font-semibold py-1 rounded cursor-not-allowed">
            Contact
          </span>
        )}
      </div>
    </motion.div>
  );
}

export default function DJs() {
  const [query, setQuery] = useState("");
  const mc = useManagedCollection<any>("djs", DJS, (d, i) => slugify(d.name || String(i)));

  const filtered = mc.items.filter(
    (d) =>
      query.trim() === "" || d.name.toLowerCase().includes(query.trim().toLowerCase())
  );

  return (
    <div className="min-h-screen bg-white">
      <CategoryHero
        title="DJ's"
        placeholder="Search DJ's…"
        backgroundImage="https://storage.googleapis.com/happyspace-generated-images/generated/2026-06-25/8077ac7ac47b.png"
        query={query}
        setQuery={setQuery}
      />
      <NewsTicker />

      <section className="bg-gray-50/40 py-8" style={{ fontFamily: "'Poppins', sans-serif" }}>
        <div className="max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 flex flex-col lg:flex-row gap-6">
          {/* LEFT: DJs */}
          <div className="flex-1 min-w-0">
            <h2 className="text-[16px] md:text-[18px] font-bold text-gray-900 mb-1">DJ's in Dallas–DFW</h2>
            <p className="text-[13px] text-gray-500 mb-5">Discover the best DJ's in the DFW area.</p>
            <AdminAddBar mc={mc} fields={DJ_FIELDS} label="Add DJ" />
            {filtered.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-1.5">
                {filtered.map((d, i) => (
                  <AdminItem key={d._key} mc={mc} item={d} fields={DJ_FIELDS} title="DJ">
                    <DJCard dj={d as any} index={i} href={`/djs/${d._key}`} />
                  </AdminItem>
                ))}
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="bg-white border border-dashed border-gray-200 rounded-2xl p-12 text-center"
              >
                <p className="text-[15px] text-gray-500 mb-2">DJ listings coming soon.</p>
                <p className="text-[12px] text-gray-400">
                  Know a great DJ?{" "}
                  <a href="/add-business" className="text-[#f97316] font-semibold hover:underline">
                    Add your business
                  </a>
                </p>
              </motion.div>
            )}
          </div>

          {/* Instagram widget removed — only on home page */}
        </div>
      </section>
    </div>
  );
}
